/**
 * The animation timeline (bottom panel): one timeline split into phases that map to OGraf actions (In, step
 * transitions, hold loops, Out, Update and its per-field sub-animations, custom actions). Per element and property
 * keyframe tracks with click/drag/marquee editing, copy/paste, easing presets and a bezier curve editor; a
 * zoomable ruler with frame-snapped scrubbing; play/pause of the open phase or the whole graphic ("play through")
 * on the canvas, rendered by the graphic runtime exactly as goToTime renders it.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { ChevronsRight, Pause, Play, Plus, SkipBack, SkipForward, StepBack, StepForward, Trash2, ZoomIn, ZoomOut } from 'lucide-react';
import { EASING_PRESETS, actionDurationsOf, frameToMs, type Easing, type Phase, type Scene, type Track } from '@prism-graphics/scene-schema';
import { t } from '../i18n.ts';
import { previewSchedule, type PreviewTarget } from '../model/preview.ts';
import { DEFAULT_DURATION, HAS_DIRECTION, PRESETS, TEXT_ONLY, applyPreset, modeFor, type Direction, type PresetKind } from '../model/presets.ts';
import {
  addCustomAction,
  addStep,
  copyKeysIn,
  deleteKeysIn,
  frameOf,
  getPhase,
  keysInRange,
  listPhases,
  moveKeysIn,
  parseKeyClip,
  pasteKeysIn,
  phaseKey,
  removeCustomAction,
  removeStep,
  removeTrack,
  samePhase,
  setAutoOutAfter,
  setEasingIn,
  setFieldTransition,
  setKeyAt,
  setPhase,
  setPhaseDuration,
  setSting,
  setUpdateTiming,
  snap,
  stepFrames,
  timeLabel,
  updateCustomAction,
  updateStep,
  valueAt,
  type KeyRef,
  type PhaseRef,
} from '../model/timeline.ts';
import { findElement, walk } from '../model/tree.ts';
import { activeLayout, sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { keyOverridden, phasePathOf, primaryOf } from '../model/layouts.ts';
import { IconButton, NumberField, SelectField, TextField, Toggle, onTablistKeyDown, tabProps } from '../ui/controls.tsx';
import { CurveEditor } from './CurveEditor.tsx';

const ROW_H = 22;
const LEFT = 12;
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

export function phaseLabel(scene: Scene, ref: PreviewTarget): string {
  switch (ref.kind) {
    case 'in':
      return t('timeline.phase.in');
    case 'out':
      return t('timeline.phase.out');
    case 'update':
      return t('timeline.phase.update');
    case 'through':
      return t('timeline.phase.through');
    case 'step':
      return t('timeline.phase.step', { name: scene.timeline.steps[ref.index]?.name || String(ref.index + 1) });
    case 'loop':
      return t('timeline.phase.loop', { name: scene.timeline.steps[ref.index]?.name || String(ref.index + 1) });
    case 'field':
      return t('timeline.phase.field', { name: scene.fields.find((f) => f.id === ref.field)?.title ?? ref.field });
    case 'custom':
      return t('timeline.phase.custom', { name: scene.timeline.customActions[ref.index]?.name ?? '' });
  }
}

type Row = { kind: 'element'; id: string } | { kind: 'track'; index: number; track: Track };

type LaneDrag =
  | { kind: 'keys'; base: Scene; x0: number; keys: KeyRef[]; moved: boolean }
  | { kind: 'marquee'; x0: number; y0: number; x1: number; y1: number; additive: KeyRef[] }
  | { kind: 'scrub' };

function fmt(ms: number, scene: Scene, format: 'frames' | 'timecode' | 'ms'): string {
  const l = timeLabel(ms, scene.canvas.frameRate);
  return format === 'frames' ? l.frames : format === 'timecode' ? l.timecode : l.ms;
}

export function TimelinePanel({ store }: { store: EditorStore }) {
  const scene = useEditor(store, sceneView);
  const anim = useEditor(store, (s) => s.anim);
  const ui = useEditor(store, (s) => s.ui);
  const selection = useEditor(store, (s) => s.selection);
  const testData = useEditor(store, (s) => s.testData);
  const rate = scene.canvas.frameRate;
  const target = anim.target;
  const ref = target.kind === 'through' ? undefined : (target as PhaseRef);
  const phase = ref ? getPhase(scene, ref) : undefined;
  // In a layout other than the primary, keyframes whose value differs there are drawn outlined (ADR 0037).
  const doc = useEditor(store, (s) => s.history.present);
  const layout = useEditor(store, activeLayout);
  const phasePath = ref && layout !== primaryOf(doc) ? phasePathOf(scene, ref) : undefined;
  const preview = useMemo(() => previewSchedule(scene, target, testData), [scene, target, testData]);
  const duration = preview.duration;
  const pxPerMs = ui.timelineZoom / 1000;
  const lanesRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<LaneDrag>();
  const dragRef = useRef<LaneDrag | undefined>(undefined);
  const [laneWidth, setLaneWidth] = useState(600);

  // A phase that no longer exists (step or action removed, undo): fall back to In.
  useEffect(() => {
    if (ref && !getPhase(scene, ref)) store.setAnim({ target: { kind: 'in' }, time: 0, keys: [], playing: false });
  }, [scene, ref, store]);

  useEffect(() => {
    const el = lanesRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setLaneWidth(el.clientWidth));
    ro.observe(el);
    setLaneWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Playback: frame-accurate (each displayed time is a frame start at the canvas rate); loops wrap.
  useEffect(() => {
    if (!anim.playing) return;
    let raf = 0;
    const startTime = anim.time >= duration - 0.5 && !preview.loops ? 0 : anim.time;
    const t0 = performance.now();
    const tick = () => {
      let tt = startTime + (performance.now() - t0);
      if (preview.loops && duration > 0) tt %= duration;
      else if (tt >= duration) {
        store.setAnim({ time: duration, playing: false });
        return;
      }
      store.setAnim({ time: frameToMs(frameOf(tt, rate), rate) });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anim.playing]);

  const setTime = (ms: number) => store.setAnim({ time: Math.min(Math.max(0, snap(ms, rate)), Math.max(duration, 0)), playing: false });
  const openPhase = (r: PreviewTarget) => store.setAnim({ target: r, time: 0, keys: [], playing: false });
  const editPhase = (fn: (p: Phase) => Phase, key?: string) => ref && store.apply((s) => setPhase(s, ref, fn(getPhase(s, ref)!)), key ? { key } : {});

  // Rows: per element (scene order) its tracks in this phase; selected elements show even without tracks.
  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    if (!phase) return out;
    const order: string[] = [];
    walk(scene.elements, (el) => order.push(el.id));
    const ids = order.filter((id) => phase.tracks.some((tr) => tr.element === id) || selection.includes(id));
    for (const id of ids) {
      out.push({ kind: 'element', id });
      phase.tracks.forEach((track, index) => track.element === id && out.push({ kind: 'track', index, track }));
    }
    return out;
  }, [phase, scene.elements, selection]);

  const timeAtX = (clientX: number) => {
    const el = lanesRef.current!;
    const r = el.getBoundingClientRect();
    return (clientX - r.left + el.scrollLeft - LEFT) / pxPerMs;
  };
  const xOf = (ms: number) => LEFT + ms * pxPerMs;
  const selected = new Set(anim.keys.map((k) => `${k.track}:${k.key}`));
  const validKeys = (keys: KeyRef[]) => keys.filter((k) => phase?.tracks[k.track]?.keyframes[k.key]);
  const keys = validKeys(anim.keys);

  // ------------------------------------------------------------------------------------------------------------
  // Keyboard (timeline focused): play, frame step, delete, copy/paste, select all.

  const copyKeys = () => {
    if (!phase || keys.length === 0) return false;
    const clip = copyKeysIn(phase, keys);
    if (!clip) return false;
    store.keyClip = clip;
    void navigator.clipboard?.writeText(JSON.stringify(clip)).catch(() => undefined);
    return true;
  };
  const pasteKeys = (clipText?: string) => {
    const clip = parseKeyClip(clipText) ?? store.keyClip;
    if (!clip || !ref) return;
    const onto = selection.length === 1 && clip.tracks.every((c) => c.element === clip.tracks[0]!.element) ? selection[0] : undefined;
    store.apply((s) => setPhase(s, ref, pasteKeysIn(s, getPhase(s, ref)!, clip, anim.time, onto)));
  };
  const deleteKeys = () => {
    if (!ref || keys.length === 0) return;
    store.apply((s) => setPhase(s, ref, deleteKeysIn(getPhase(s, ref)!, keys)));
    store.setAnim({ keys: [] });
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if ((e.target as HTMLElement).closest('input, textarea, select')) return;
    const mod = isMac ? e.metaKey : e.ctrlKey;
    let handled = true;
    // Keyboard operation of keyframes: Alt+←/→ moves the selected keys by a frame (Shift: 10 frames).
    if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      if (ref && phase && keys.length > 0) {
        const frames = (e.key === 'ArrowLeft' ? -1 : 1) * (e.shiftKey ? 10 : 1);
        const r = moveKeysIn(phase, keys, frames * (1000 * rate.den) / rate.num, rate);
        store.apply((s) => setPhase(s, ref, r.phase), { key: 'timeline.keys.nudge' });
        store.setAnim({ keys: r.keys });
      }
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key === ' ') store.setAnim({ playing: !anim.playing });
    else if (e.key === 'ArrowLeft' || e.key === ',') setTime(stepFrames(anim.time, e.shiftKey ? -10 : -1, rate));
    else if (e.key === 'ArrowRight' || e.key === '.') setTime(stepFrames(anim.time, e.shiftKey ? 10 : 1, rate));
    else if (e.key === 'Home') setTime(0);
    else if (e.key === 'End') setTime(duration);
    else if ((e.key === 'Delete' || e.key === 'Backspace') && keys.length > 0) deleteKeys();
    else if (mod && e.key.toLowerCase() === 'c' && keys.length > 0) copyKeys();
    else if (mod && e.key.toLowerCase() === 'v' && (store.keyClip || navigator.clipboard)) {
      if (navigator.clipboard?.readText) void navigator.clipboard.readText().then(pasteKeys, () => pasteKeys());
      else pasteKeys();
    } else if (mod && e.key.toLowerCase() === 'a' && phase) store.setAnim({ keys: phase.tracks.flatMap((tr, ti) => tr.keyframes.map((_, ki) => ({ track: ti, key: ki }))) });
    else if (e.key === 'Escape' && keys.length > 0) store.setAnim({ keys: [] });
    else handled = false;
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // ------------------------------------------------------------------------------------------------------------
  // Lanes: keys (click, shift-click, drag), marquee, double-click adds a key, ruler scrubbing.

  const begin = (d: LaneDrag | undefined) => {
    dragRef.current = d;
    setDrag(d);
  };

  const onKeyPointerDown = (e: ReactPointerEvent, k: KeyRef) => {
    e.stopPropagation();
    lanesRef.current?.setPointerCapture(e.pointerId);
    lanesRef.current?.focus({ preventScroll: true });
    const id = `${k.track}:${k.key}`;
    let next = keys;
    if (e.shiftKey) next = selected.has(id) ? keys.filter((x) => `${x.track}:${x.key}` !== id) : [...keys, k];
    else if (!selected.has(id)) next = [k];
    store.setAnim({ keys: next, playing: false });
    if (!e.shiftKey) begin({ kind: 'keys', base: scene, x0: e.clientX, keys: next, moved: false });
  };

  const onLanePointerDown = (e: ReactPointerEvent) => {
    if (e.button !== 0 || !lanesRef.current) return;
    lanesRef.current.setPointerCapture(e.pointerId);
    lanesRef.current.focus({ preventScroll: true });
    const r = lanesRef.current.getBoundingClientRect();
    const onRuler = e.clientY - r.top < 24;
    if (onRuler) {
      setTime(timeAtX(e.clientX));
      begin({ kind: 'scrub' });
      return;
    }
    const x = e.clientX - r.left + lanesRef.current.scrollLeft;
    const y = e.clientY - r.top + lanesRef.current.scrollTop;
    if (!e.shiftKey) store.setAnim({ keys: [] });
    setTime(timeAtX(e.clientX));
    begin({ kind: 'marquee', x0: x, y0: y, x1: x, y1: y, additive: e.shiftKey ? keys : [] });
  };

  const onLanePointerMove = (e: ReactPointerEvent) => {
    const d = dragRef.current;
    if (!d || !lanesRef.current || !ref) return;
    if (d.kind === 'scrub') return setTime(timeAtX(e.clientX));
    if (d.kind === 'keys') {
      const dt = (e.clientX - d.x0) / pxPerMs;
      if (!d.moved && Math.abs(e.clientX - d.x0) < 3) return;
      const r = moveKeysIn(getPhase(d.base, ref)!, d.keys, dt, rate);
      store.preview(setPhase(d.base, ref, r.phase));
      store.setAnim({ keys: r.keys });
      dragRef.current = { ...d, moved: true };
      return;
    }
    const r = lanesRef.current.getBoundingClientRect();
    const next = { ...d, x1: e.clientX - r.left + lanesRef.current.scrollLeft, y1: e.clientY - r.top + lanesRef.current.scrollTop };
    begin(next);
    if (!phase) return;
    const top = Math.min(next.y0, next.y1) - 24;
    const bottom = Math.max(next.y0, next.y1) - 24;
    const tracks = rows.flatMap((row, i) => (row.kind === 'track' && (i + 1) * ROW_H > top && i * ROW_H < bottom ? [row.index] : []));
    const found = keysInRange(phase, tracks, (next.x0 - LEFT) / pxPerMs, (next.x1 - LEFT) / pxPerMs);
    const all = [...d.additive, ...found.filter((k) => !d.additive.some((a) => a.track === k.track && a.key === k.key))];
    store.setAnim({ keys: all });
  };

  const onLanePointerUp = (e: ReactPointerEvent) => {
    const d = dragRef.current;
    begin(undefined);
    if (lanesRef.current?.hasPointerCapture(e.pointerId)) lanesRef.current.releasePointerCapture(e.pointerId);
    if (d?.kind === 'keys' && d.moved) store.commitFrom(d.base, store.scene);
  };

  const onLaneDoubleClick = (e: React.MouseEvent) => {
    if (!ref || !phase || !lanesRef.current) return;
    const r = lanesRef.current.getBoundingClientRect();
    const y = e.clientY - r.top + lanesRef.current.scrollTop - 24;
    const row = rows[Math.floor(y / ROW_H)];
    if (row?.kind !== 'track') return;
    const time = snap(timeAtX(e.clientX), rate);
    const tr = row.track;
    store.apply((s) => setKeyAt(s, ref, tr.element, tr.property, time, valueAt(s, getPhase(s, ref), tr.element, tr.property, time)));
  };

  const onWheel = (e: React.WheelEvent) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    store.setUi({ timelineZoom: Math.min(4000, Math.max(30, ui.timelineZoom * (e.deltaY < 0 ? 1.2 : 1 / 1.2))) });
  };

  // ------------------------------------------------------------------------------------------------------------
  // Ruler ticks: a frame step that keeps labels at least ~64 px apart.

  const frameMs = 1000 / (rate.num / rate.den);
  const fpsRound = Math.round(rate.num / rate.den);
  const stepChoices = [1, 2, 5, 10, fpsRound / 2, fpsRound, fpsRound * 2, fpsRound * 5, fpsRound * 10, fpsRound * 30, fpsRound * 60].filter((n) => Number.isInteger(n) && n > 0);
  const tickFrames = stepChoices.find((n) => n * frameMs * pxPerMs >= 64) ?? stepChoices[stepChoices.length - 1]!;
  const visibleMs = Math.max(duration, (laneWidth - LEFT * 2) / pxPerMs);
  const ticks: number[] = [];
  for (let f = 0; frameToMs(f, rate) <= visibleMs + 0.5; f += tickFrames) ticks.push(f);
  const contentW = Math.max(laneWidth, xOf(visibleMs) + LEFT);

  const tl = timeLabel(anim.time, rate);
  const durations = actionDurationsOf(scene);
  const stepCount = scene.timeline.steps.length;
  const sting = stepCount === 0;
  const phases = listPhases(scene);

  return (
    <div className="timeline" data-testid="timeline" onKeyDown={onKeyDown}>
      <div className="tl-phases">
        <div className="tl-phase-tabs" role="tablist" aria-label={t('timeline.phases')} onKeyDown={onTablistKeyDown}>
        {phases.map((r) => {
          const p = getPhase(scene, r);
          return (
            <button
              key={phaseKey(r)}
              type="button"
              {...tabProps(target.kind !== 'through' && samePhase(target as PhaseRef, r), 'tl-phase-panel')}
              className={`tl-phase k-${r.kind}${target.kind !== 'through' && samePhase(target as PhaseRef, r) ? ' active' : ''}`}
              data-testid={`phase-${r.kind}${'index' in r ? `-${r.index}` : 'field' in r ? `-${r.field}` : ''}`}
              onClick={() => openPhase(r)}
            >
              <span>{phaseLabel(scene, r)}</span>
              <span className="tl-dur">{fmt(p?.duration ?? 0, scene, ui.timeFormat)}</span>
            </button>
          );
        })}
        <button type="button" {...tabProps(target.kind === 'through', 'tl-phase-panel')} className={`tl-phase k-through${target.kind === 'through' ? ' active' : ''}`} data-testid="phase-through" onClick={() => openPhase({ kind: 'through' })}>
          <ChevronsRight size={13} aria-hidden /> {t('timeline.phase.through')}
        </button>
        </div>
        <span className="spacer" />
        <span className="tl-stepcount" data-testid="step-count">
          {t('timeline.stepCount', { count: stepCount })}
        </span>
        <button type="button" className="btn small" data-testid="add-step" disabled={sting} onClick={() => store.apply((s) => addStep(s))}>
          <Plus size={13} aria-hidden /> {t('timeline.addStep')}
        </button>
        <button
          type="button"
          className="btn small"
          data-testid="add-custom-action"
          onClick={() => {
            const r = addCustomAction(scene, t('timeline.newAction'));
            store.apply(() => r.scene);
            openPhase({ kind: 'custom', index: r.index });
          }}
        >
          <Plus size={13} aria-hidden /> {t('timeline.addAction')}
        </button>
        <Toggle label={t('timeline.sting')} checked={sting} testId="sting-toggle" onChange={(on) => store.apply((s) => setSting(s, on))} />
      </div>

      <div className="tl-toolbar">
        <IconButton label={t('timeline.toStart')} onClick={() => setTime(0)}>
          <SkipBack size={14} />
        </IconButton>
        <IconButton label={t('timeline.prevFrame')} onClick={() => setTime(stepFrames(anim.time, -1, rate))} testId="frame-back">
          <StepBack size={14} />
        </IconButton>
        <IconButton label={anim.playing ? t('timeline.pause') : t('timeline.play')} pressed={anim.playing} onClick={() => store.setAnim({ playing: !anim.playing })} testId="play-phase">
          {anim.playing ? <Pause size={14} /> : <Play size={14} />}
        </IconButton>
        <IconButton label={t('timeline.nextFrame')} onClick={() => setTime(stepFrames(anim.time, 1, rate))} testId="frame-forward">
          <StepForward size={14} />
        </IconButton>
        <IconButton label={t('timeline.toEnd')} onClick={() => setTime(duration)}>
          <SkipForward size={14} />
        </IconButton>
        <span className="tl-time" data-testid="playhead-time" data-ms={anim.time} aria-live="off">
          <b>{tl.frames}</b> <span>{tl.timecode}</span> <span className="muted">{tl.ms}</span>
        </span>
        <span className="tl-sep" />
        {ref && phase && (
          <NumberField
            label={t('timeline.duration')}
            short={t('timeline.durationShort')}
            value={Math.round(phase.duration)}
            min={0}
            suffix="ms"
            testId="phase-duration"
            onChange={(ms) => store.apply((s) => setPhaseDuration(s, ref, ms), { key: `dur:${phaseKey(ref)}` })}
          />
        )}
        <Toggle label={t('timeline.autoKey')} checked={ui.autoKey} testId="auto-key" onChange={(autoKey) => store.setUi({ autoKey })} />
        <span className="spacer" />
        <SelectField
          label={t('timeline.timeFormat')}
          value={ui.timeFormat}
          options={[
            { value: 'frames', label: t('timeline.format.frames') },
            { value: 'timecode', label: t('timeline.format.timecode') },
            { value: 'ms', label: t('timeline.format.ms') },
          ]}
          onChange={(timeFormat) => store.setUi({ timeFormat })}
        />
        <IconButton label={t('timeline.zoomOut')} onClick={() => store.setUi({ timelineZoom: Math.max(30, ui.timelineZoom / 1.5) })} testId="tl-zoom-out">
          <ZoomOut size={14} />
        </IconButton>
        <IconButton label={t('timeline.zoomIn')} onClick={() => store.setUi({ timelineZoom: Math.min(4000, ui.timelineZoom * 1.5) })} testId="tl-zoom-in">
          <ZoomIn size={14} />
        </IconButton>
      </div>

      <div className="tl-body" id="tl-phase-panel" role="tabpanel" aria-label={t('timeline.phases')}>
        <div className="tl-heads" style={{ paddingTop: 24 }}>
          {rows.map((row) =>
            row.kind === 'element' ? (
              <button key={`e-${row.id}`} type="button" className={`tl-head el${selection.includes(row.id) ? ' selected' : ''}`} style={{ height: ROW_H }} onClick={() => store.select([row.id])}>
                {findElement(scene, row.id)?.name || row.id}
              </button>
            ) : (
              <div key={`t-${row.index}`} className="tl-head track" style={{ height: ROW_H }} data-testid="track-head" data-property={row.track.property} data-element={row.track.element}>
                <span>{t(`anim.prop.${row.track.property}`)}</span>
                {row.track.stagger && <span className="badge">{t(`timeline.stagger.${row.track.stagger.by}`)}</span>}
                {row.track.property === 'color' && (
                  <button
                    type="button"
                    className="badge toggle"
                    title={t('timeline.colorSpaceHint')}
                    aria-label={t('timeline.colorSpace', { space: row.track.colorSpace === 'oklab' ? 'OKLab' : 'sRGB' })}
                    data-testid="track-colorspace"
                    data-space={row.track.colorSpace ?? 'srgb'}
                    onClick={() =>
                      ref &&
                      editPhase((p) => ({
                        ...p,
                        tracks: p.tracks.map((tr, i) => {
                          if (i !== row.index) return tr;
                          const { colorSpace: _c, ...rest } = tr;
                          return tr.colorSpace === 'oklab' ? rest : { ...rest, colorSpace: 'oklab' as const };
                        }),
                      }))
                    }
                  >
                    {row.track.colorSpace === 'oklab' ? 'OKLab' : 'sRGB'}
                  </button>
                )}
                <IconButton label={t('timeline.removeTrack')} onClick={() => ref && editPhase((p) => removeTrack(p, row.index))}>
                  <Trash2 size={12} />
                </IconButton>
              </div>
            ),
          )}
          {ref && rows.length === 0 && <p className="hint pad">{t('timeline.empty')}</p>}
          {!ref && <p className="hint pad">{t('timeline.throughHint')}</p>}
        </div>
        <div
          ref={lanesRef}
          className="tl-lanes"
          tabIndex={0}
          role="application"
          aria-label={t('timeline.lanes')}
          data-testid="timeline-lanes"
          onPointerDown={onLanePointerDown}
          onPointerMove={onLanePointerMove}
          onPointerUp={onLanePointerUp}
          onPointerCancel={onLanePointerUp}
          onDoubleClick={onLaneDoubleClick}
          onWheel={onWheel}
        >
          <div className="tl-content" style={{ width: contentW, height: 24 + rows.length * ROW_H }}>
            <div className="tl-ruler" data-testid="timeline-ruler">
              {ticks.map((f) => (
                <span key={f} className="tl-tick" style={{ left: xOf(frameToMs(f, rate)) }}>
                  {fmt(frameToMs(f, rate), scene, ui.timeFormat)}
                </span>
              ))}
              <span className="tl-end" style={{ left: xOf(duration) }} title={t('timeline.phaseEnd')} />
              {preview.marks.map((m) => (
                <span key={`${m.label}${m.t}`} className="tl-mark" style={{ left: xOf(m.t) }}>
                  {m.label}
                </span>
              ))}
            </div>
            <div className="tl-span" style={{ left: xOf(0), width: duration * pxPerMs, height: rows.length * ROW_H }} />
            {rows.map((row, i) =>
              row.kind === 'track' ? (
                <div key={`l-${row.index}`} className="tl-lane" style={{ top: 24 + i * ROW_H, height: ROW_H }} data-testid="track-lane" data-property={row.track.property} data-element={row.track.element}>
                  {row.track.keyframes.map((kf, ki) => {
                    const next = row.track.keyframes[ki + 1];
                    return next ? <span key={`s${ki}`} className={`tl-seg${kf.easing === 'hold' ? ' hold' : ''}`} style={{ left: xOf(kf.time), width: (next.time - kf.time) * pxPerMs }} /> : null;
                  })}
                  {row.track.keyframes.map((kf, ki) => {
                    const overridden = keyOverridden(doc, layout, phasePath, row.track, kf.time);
                    return (
                    <button
                      key={`k${ki}`}
                      type="button"
                      className={`tl-key${selected.has(`${row.index}:${ki}`) ? ' selected' : ''}${overridden ? ' overridden' : ''}`}
                      style={{ left: xOf(kf.time) }}
                      data-testid="keyframe"
                      data-time={Math.round(kf.time * 1000) / 1000}
                      data-overridden={overridden ? 'true' : undefined}
                      title={overridden ? t('layouts.keyOverridden') : undefined}
                      aria-label={t('timeline.keyframe', { property: t(`anim.prop.${row.track.property}`), time: fmt(kf.time, scene, ui.timeFormat) })}
                      aria-pressed={selected.has(`${row.index}:${ki}`)}
                      onPointerDown={(e) => onKeyPointerDown(e, { track: row.index, key: ki })}
                      onKeyDown={(e) => {
                        // Enter or Space selects the keyframe (Shift adds it to the selection); the lanes' keys
                        // (Alt+arrows to move, Delete, copy and paste) then act on the selection.
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        const k = { track: row.index, key: ki };
                        const id = `${k.track}:${k.key}`;
                        store.setAnim({ keys: e.shiftKey ? (selected.has(id) ? keys.filter((x) => `${x.track}:${x.key}` !== id) : [...keys, k]) : [k], playing: false });
                      }}
                    />
                    );
                  })}
                </div>
              ) : (
                <div key={`le-${row.id}`} className="tl-lane el" style={{ top: 24 + i * ROW_H, height: ROW_H }} />
              ),
            )}
            {drag?.kind === 'marquee' && (
              <div className="tl-marquee" style={{ left: Math.min(drag.x0, drag.x1), top: Math.min(drag.y0, drag.y1), width: Math.abs(drag.x1 - drag.x0), height: Math.abs(drag.y1 - drag.y0) }} />
            )}
            <div className="tl-playhead" style={{ left: xOf(anim.time) }} data-testid="playhead" />
          </div>
        </div>
        <aside className="tl-side" aria-label={t('timeline.side')}>
          {ref && phase && keys.length > 0 && <EasingBox store={store} ref_={ref} phase={phase} keys={keys} />}
          {ref && phase && <PresetBox store={store} ref_={ref} />}
          {target.kind !== 'through' && <PhaseSettings store={store} target={target as PhaseRef} />}
        </aside>
      </div>

      <footer className="tl-foot" data-testid="action-durations">
        <span className="muted">{t('timeline.manifest')}</span>
        <code>stepCount {stepCount}</code>
        {durations.map((d) => (
          <code key={d.type + ('customActionId' in d ? d.customActionId : '')} data-testid={`duration-${d.type}`}>
            {d.type === 'customAction' ? `customAction ${d.customActionId}` : d.type} {d.duration} ms
            {'steps' in d && d.steps && d.steps.length > 1 ? ` (${d.steps.map((s) => `${s.step}: ${s.duration}`).join(', ')})` : ''}
          </code>
        ))}
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Side boxes

function PresetBox({ store, ref_ }: { store: EditorStore; ref_: PhaseRef }) {
  const scene = useEditor(store, sceneView);
  const selection = useEditor(store, (s) => s.selection);
  const time = useEditor(store, (s) => s.anim.time);
  const [kind, setKind] = useState<PresetKind>('fade');
  const [direction, setDirection] = useState<Direction>('right');
  const [duration, setDuration] = useState<number | undefined>();
  const mode = modeFor(ref_);
  const textOnly = TEXT_ONLY.has(kind);
  const usable = selection.filter((id) => !textOnly || findElement(scene, id)?.type === 'text');
  return (
    <section className="tl-box" aria-label={t('timeline.presets')}>
      <h3>{t('timeline.presets')}</h3>
      <SelectField label={t('timeline.preset')} value={kind} testId="preset-kind" options={PRESETS.map((p) => ({ value: p, label: t(`preset.${p}`) }))} onChange={setKind} />
      {HAS_DIRECTION.has(kind) && (
        <SelectField
          label={t('timeline.direction')}
          value={direction}
          testId="preset-direction"
          options={(['left', 'right', 'up', 'down'] as const).map((d) => ({ value: d, label: t(`direction.${d}`) }))}
          onChange={setDirection}
        />
      )}
      <NumberField label={t('timeline.presetDuration')} value={duration ?? DEFAULT_DURATION[kind]} min={0} suffix="ms" testId="preset-duration" onChange={setDuration} />
      <button
        type="button"
        className="btn small primary"
        data-testid="apply-preset"
        disabled={usable.length === 0}
        onClick={() => store.apply((s) => applyPreset(s, ref_, usable, kind, { mode, direction, start: time, ...(duration !== undefined ? { duration } : {}) }))}
      >
        {t(mode === 'out' ? 'timeline.applyOut' : 'timeline.applyIn')}
      </button>
      <p className="hint">{usable.length === 0 ? t(textOnly ? 'timeline.presetNeedsText' : 'timeline.presetNeedsSelection') : t('timeline.presetHint', { count: usable.length })}</p>
    </section>
  );
}

function EasingBox({ store, ref_, phase, keys }: { store: EditorStore; ref_: PhaseRef; phase: Phase; keys: KeyRef[] }) {
  const first = phase.tracks[keys[0]!.track]?.keyframes[keys[0]!.key];
  const easing = first?.easing ?? 'linear';
  const value = typeof easing === 'string' ? easing : 'custom';
  const baseRef = useRef<Scene | undefined>(undefined);
  const set = (e: Easing) => store.apply((s) => setPhase(s, ref_, setEasingIn(getPhase(s, ref_)!, keys, e)));
  return (
    <section className="tl-box" aria-label={t('timeline.easing')}>
      <h3>{t('timeline.easingCount', { count: keys.length })}</h3>
      <SelectField
        label={t('timeline.easing')}
        value={value}
        testId="key-easing"
        options={[...EASING_PRESETS.map((p) => ({ value: p, label: t(`easing.${p}`) })), { value: 'custom', label: t('easing.custom') }]}
        onChange={(v) => v !== 'custom' && set(v as Easing)}
      />
      {easing !== 'hold' && (
        <CurveEditor
          easing={easing}
          onChange={(b) => {
            const base = (baseRef.current ??= store.scene);
            store.preview(setPhase(base, ref_, setEasingIn(getPhase(base, ref_)!, keys, { cubicBezier: b })));
          }}
          onCommit={() => {
            if (baseRef.current) store.commitFrom(baseRef.current, store.scene);
            baseRef.current = undefined;
          }}
        />
      )}
      <p className="hint">{t('timeline.easingHint')}</p>
    </section>
  );
}

function PhaseSettings({ store, target }: { store: EditorStore; target: PhaseRef }) {
  const scene = useEditor(store, sceneView);
  const tl = scene.timeline;
  const rate = scene.canvas.frameRate;
  if (target.kind === 'in' && tl.steps.length === 0) {
    return (
      <section className="tl-box">
        <h3>{t('timeline.stingSettings')}</h3>
        <NumberField label={t('timeline.autoOutAfter')} value={tl.autoOutAfter ?? 0} min={0} suffix="ms" testId="auto-out-after" onChange={(ms) => store.apply((s) => setAutoOutAfter(s, ms), { key: 'autoOut' })} />
        <p className="hint">{t('timeline.stingHint')}</p>
      </section>
    );
  }
  if (target.kind === 'step' || target.kind === 'loop') {
    const step = tl.steps[target.index];
    if (!step) return null;
    return (
      <section className="tl-box">
        <h3>{t('timeline.stepSettings', { n: target.index })}</h3>
        <TextField label={t('timeline.stepName')} value={step.name} onChange={(name) => store.apply((s) => updateStep(s, target.index, { name }), { key: `step-name:${target.index}` })} />
        <Toggle
          label={t('timeline.holdLoop')}
          checked={step.loop !== undefined}
          testId="hold-loop"
          onChange={(on) => store.apply((s) => updateStep(s, target.index, { loop: on ? { duration: snap(1000, rate), tracks: [] } : undefined }))}
        />
        <p className="hint">{t('timeline.loopHint')}</p>
        {tl.steps.length > 1 && (
          <button type="button" className="btn small danger" data-testid="remove-step" onClick={() => store.apply((s) => removeStep(s, target.index))}>
            {t('timeline.removeStep')}
          </button>
        )}
      </section>
    );
  }
  if (target.kind === 'update' || target.kind === 'field') {
    return (
      <section className="tl-box">
        <h3>{t('timeline.updateSettings')}</h3>
        <NumberField label={t('timeline.updateDuration')} value={tl.update.duration} min={0} suffix="ms" onChange={(duration) => store.apply((s) => setUpdateTiming(s, { duration }), { key: 'upd-dur' })} />
        <SelectField label={t('timeline.updateEasing')} value={typeof tl.update.easing === 'string' ? tl.update.easing : 'easeInOutCubic'} options={EASING_PRESETS.map((p) => ({ value: p, label: t(`easing.${p}`) }))} onChange={(easing) => store.apply((s) => setUpdateTiming(s, { easing }))} />
        <p className="hint">{t('timeline.autoSizeHint')}</p>
        {scene.fields.map((f) => (
          <SelectField
            key={f.id}
            label={f.title}
            value={tl.update.fields[f.id]?.mode ?? 'cut'}
            testId={`transition-${f.id}`}
            options={[
              { value: 'cut', label: t('transition.cut') },
              { value: 'crossfade', label: t('transition.crossfade') },
              { value: 'phase', label: t('transition.phase') },
            ]}
            onChange={(mode) => store.apply((s) => setFieldTransition(s, f.id, mode))}
          />
        ))}
        {scene.fields.length === 0 && <p className="hint">{t('timeline.noFields')}</p>}
      </section>
    );
  }
  if (target.kind === 'custom') {
    const a = tl.customActions[target.index];
    if (!a) return null;
    return <CustomActionSettings store={store} index={target.index} />;
  }
  return null;
}

function CustomActionSettings({ store, index }: { store: EditorStore; index: number }) {
  const scene = useEditor(store, sceneView);
  const a = scene.timeline.customActions[index]!;
  const [schemaText, setSchemaText] = useState(a.schema ? JSON.stringify(a.schema, null, 2) : '');
  const [schemaError, setSchemaError] = useState<string>();
  const idOk = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(a.id);
  return (
    <section className="tl-box">
      <h3>{t('timeline.customSettings')}</h3>
      <TextField label={t('timeline.actionName')} value={a.name} testId="action-name" onChange={(name) => name.trim() && store.apply((s) => updateCustomAction(s, index, { name }), { key: `act-name:${index}` })} />
      <TextField
        label={t('timeline.actionId')}
        value={a.id}
        testId="action-id"
        onChange={(id) => /^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(id) && !scene.timeline.customActions.some((x, i) => i !== index && x.id === id) && store.apply((s) => updateCustomAction(s, index, { id }), { key: `act-id:${index}` })}
      />
      {!idOk && <p className="hint warn">{t('timeline.actionIdHint')}</p>}
      <TextField
        label={t('timeline.actionSchema')}
        multiline
        rows={4}
        value={schemaText}
        placeholder={'{ "type": "object", "properties": { … } }'}
        onChange={(text) => {
          setSchemaText(text);
          if (!text.trim()) {
            setSchemaError(undefined);
            store.apply((s) => updateCustomAction(s, index, { schema: null }), { key: `act-schema:${index}` });
            return;
          }
          try {
            const v = JSON.parse(text) as unknown;
            if (typeof v !== 'object' || v === null || Array.isArray(v)) throw new Error(t('timeline.schemaObject'));
            setSchemaError(undefined);
            store.apply((s) => updateCustomAction(s, index, { schema: v as Record<string, unknown> }), { key: `act-schema:${index}` });
          } catch (e) {
            setSchemaError(e instanceof Error ? e.message : String(e));
          }
        }}
      />
      {schemaError && <p className="hint warn">{schemaError}</p>}
      <button type="button" className="btn small danger" onClick={() => store.apply((s) => removeCustomAction(s, index))}>
        {t('timeline.removeAction')}
      </button>
    </section>
  );
}
